import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiEye, FiShoppingCart, FiCheck,
  FiAlertCircle, FiSave,
  FiZap, FiPlay, FiX, FiGlobe, FiTag,
  FiRefreshCw, FiChevronDown, FiChevronUp,
} from "react-icons/fi";
import { FaFacebookF } from "react-icons/fa";
import FloatingAdminMenu from "./FloatMenu";

const CURSOS_API = "https://www.medicinacondante.com/cursos.php";

/* ─────────────────────────────────────
   ESTILOS GLOBALES
───────────────────────────────────── */
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,200;0,300;0,400;0,600;0,700;0,800;1,300&display=swap');

    :root {
      --gold:      #C9A84C;
      --gold-dim:  rgba(201,168,76,0.55);
      --gold-sub:  rgba(201,168,76,0.08);
      --gold-brd:  rgba(201,168,76,0.20);
      --bg:        #0A0906;
      --bg-2:      #0F0E0B;
      --bg-3:      #141208;
      --line:      rgba(201,168,76,0.12);
      --ink:       #EDE0C8;
      --ink-2:     rgba(237,224,200,0.62);
      --ink-3:     rgba(237,224,200,0.32);
      --green:     #6DBF8F;
      --green-dim: rgba(109,191,143,0.18);
      --green-brd: rgba(109,191,143,0.35);
      --red:       #E07A7A;
      --blue:      #6BA3E0;
      --blue-sub:  rgba(107,163,224,0.08);
      --blue-brd:  rgba(107,163,224,0.25);
    }

    *, *::before, *::after { box-sizing: border-box; }

    body {
      background: var(--bg);
      font-family: 'Plus Jakarta Sans', sans-serif;
    }

    ::placeholder {
      color: var(--ink-3);
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 300;
      font-style: italic;
    }

    textarea, input, select {
      font-family: 'Plus Jakarta Sans', sans-serif;
    }

    @keyframes adm-spin { to { transform: rotate(360deg); } }
    .adm-spin { animation: adm-spin 0.9s linear infinite; }

    @keyframes pulse-dot {
      0%, 100% { opacity: 0.45; transform: scale(1); }
      50%       { opacity: 1;   transform: scale(1.4); }
    }
    .pulse-anim { animation: pulse-dot 2s ease-in-out infinite; }

    ::-webkit-scrollbar { width: 4px; }
    ::-webkit-scrollbar-track { background: var(--bg-2); }
    ::-webkit-scrollbar-thumb { background: var(--gold-brd); border-radius: 2px; }
  `}</style>
);

/* ─────────────────────────────────────
   UI HELPERS
───────────────────────────────────── */
const SectionLabel = ({ children, style = {} }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, ...style }}>
    <span style={{ display: "inline-block", width: 20, height: 1, background: "var(--gold)", opacity: 0.7 }} />
    <span style={{ fontWeight: 700, fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ink-3)" }}>
      {children}
    </span>
  </div>
);

const Badge = ({ children, variant = "warn" }) => {
  const map = {
    warn:    { color: "var(--gold)",  borderColor: "var(--gold-brd)",  background: "var(--gold-sub)" },
    ok:      { color: "var(--green)", borderColor: "var(--green-brd)", background: "var(--green-dim)" },
    blue:    { color: "var(--blue)",  borderColor: "var(--blue-brd)",  background: "var(--blue-sub)" },
    neutral: { color: "var(--ink-3)", borderColor: "var(--line)",      background: "transparent" },
    red:     { color: "var(--red)",   borderColor: "rgba(224,122,122,0.3)", background: "rgba(224,122,122,0.07)" },
  };
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase",
      padding: "3px 10px", borderRadius: 999, border: "1px solid",
      ...map[variant],
    }}>
      {children}
    </span>
  );
};

const Hint = ({ children }) => (
  <p style={{
    fontSize: 12, fontWeight: 300, color: "var(--ink-2)", lineHeight: 1.85,
    borderLeft: "2px solid rgba(201,168,76,0.25)", paddingLeft: 14, marginTop: 10,
  }}>
    {children}
  </p>
);

const Divider = () => (
  <div style={{ height: 1, background: "var(--line)", margin: "14px 0" }} />
);

const CardTopLine = ({ color = "rgba(201,168,76,0.25)" }) => (
  <div style={{
    position: "absolute", top: 0, left: 16, right: 16, height: 1,
    background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
  }} />
);

/* ─────────────────────────────────────
   STYLED INPUT
───────────────────────────────────── */
const StyledInput = ({ value, onChange, placeholder, type = "text", mono = false }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          background: "var(--bg-2)", color: "var(--ink)",
          border: `1px solid ${focused ? "rgba(201,168,76,0.6)" : "var(--line)"}`,
          outline: "none", width: "100%", height: 40, padding: "0 12px",
          fontSize: mono ? 12 : 13,
          fontFamily: mono ? "'Courier New', monospace" : "'Plus Jakarta Sans', sans-serif",
          fontWeight: 300,
          transition: "border-color 0.25s",
          boxShadow: focused ? "0 0 0 4px rgba(201,168,76,0.04)" : "none",
          borderRadius: 0,
          letterSpacing: mono ? "0.05em" : "normal",
        }}
      />
      <AnimatePresence>
        {focused && (
          <motion.div
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 1 }}
            exit={{ scaleX: 0, opacity: 0 }}
            style={{
              position: "absolute", bottom: 0, left: 0, right: 0, height: 1,
              background: "linear-gradient(90deg, var(--gold), transparent)",
              transformOrigin: "left",
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

/* ─────────────────────────────────────
   TOGGLE
───────────────────────────────────── */
const Toggle = ({ value, onChange, pulse }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    <div
      className={value && pulse ? "pulse-anim" : ""}
      style={{
        width: 7, height: 7, borderRadius: "50%",
        background: value ? "var(--green)" : "var(--ink-3)",
        opacity: value ? 1 : 0.35,
        transition: "background 0.3s",
      }}
    />
    <button
      onClick={onChange}
      style={{
        position: "relative", width: 44, height: 24, cursor: "pointer",
        background: value ? "rgba(109,191,143,0.18)" : "var(--gold-sub)",
        border: `1px solid ${value ? "var(--green-brd)" : "var(--line)"}`,
        borderRadius: 0,
        transition: "background 0.3s, border-color 0.3s",
      }}
    >
      <motion.div
        animate={{ x: value ? 22 : 2 }}
        transition={{ type: "spring", stiffness: 500, damping: 32 }}
        style={{
          position: "absolute", top: 3, width: 16, height: 16,
          background: value ? "var(--green)" : "rgba(201,168,76,0.55)",
        }}
      />
    </button>
  </div>
);

/* ─────────────────────────────────────
   CHECK ITEM
───────────────────────────────────── */
const CheckItem = ({ label, ok }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, fontWeight: ok ? 600 : 300, color: ok ? "var(--green)" : "var(--ink-3)" }}>
    {ok
      ? <FiCheck size={14} style={{ color: "var(--green)", flexShrink: 0 }} />
      : <div style={{ width: 14, height: 14, border: "1px solid var(--line)", borderRadius: "50%", flexShrink: 0 }} />
    }
    {label}
  </div>
);

/* ─────────────────────────────────────
   PIXEL MODE SELECTOR
───────────────────────────────────── */
const PixelModeSelector = ({ mode, onChange }) => {
  const options = [
    {
      key: "global",
      icon: <FiGlobe size={16} />,
      title: "Pixel global",
      desc: "Un solo Pixel ID para todos los cursos",
      color: "var(--blue)",
      borderColor: "var(--blue-brd)",
      bg: "var(--blue-sub)",
    },
    {
      key: "individual",
      icon: <FiTag size={16} />,
      title: "Pixel por curso",
      desc: "Pixel ID distinto para cada curso",
      color: "var(--gold)",
      borderColor: "var(--gold-brd)",
      bg: "var(--gold-sub)",
    },
  ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
      {options.map((opt) => {
        const active = mode === opt.key;
        return (
          <motion.button
            key={opt.key}
            whileHover={{ opacity: 0.9 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onChange(opt.key)}
            style={{
              padding: "14px 16px",
              background: active ? opt.bg : "var(--bg-2)",
              border: `1px solid ${active ? opt.borderColor : "var(--line)"}`,
              cursor: "pointer",
              textAlign: "left",
              transition: "all 0.25s",
              position: "relative",
            }}
          >
            {active && (
              <div style={{
                position: "absolute", top: 0, left: 8, right: 8, height: 1,
                background: `linear-gradient(90deg, transparent, ${opt.borderColor}, transparent)`,
              }} />
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <div style={{
                width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center",
                background: active ? opt.bg : "transparent",
                border: `1px solid ${active ? opt.borderColor : "var(--line)"}`,
                color: active ? opt.color : "var(--ink-3)",
                transition: "all 0.25s",
              }}>
                {opt.icon}
              </div>
              <div>
                <p style={{ fontSize: 12, fontWeight: 700, color: active ? opt.color : "var(--ink-2)", marginBottom: 2 }}>
                  {opt.title}
                </p>
              </div>
              {active && (
                <motion.div
                  initial={{ scale: 0 }} animate={{ scale: 1 }}
                  style={{
                    marginLeft: "auto", width: 18, height: 18, borderRadius: "50%",
                    background: opt.color, display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  <FiCheck size={10} style={{ color: "#0A0906" }} />
                </motion.div>
              )}
            </div>
            <p style={{ fontSize: 11, fontWeight: 300, color: "var(--ink-3)", lineHeight: 1.5 }}>
              {opt.desc}
            </p>
          </motion.button>
        );
      })}
    </div>
  );
};

/* ─────────────────────────────────────
   CURSOS TABLE (pixel individual)
───────────────────────────────────── */
const CursosPixelTable = ({ cursos, pixelMap, onPixelChange, loading }) => {
  const [expanded, setExpanded] = useState(null);

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 0", color: "var(--ink-3)" }}>
        <div className="adm-spin" style={{ width: 16, height: 16, borderRadius: "50%", border: "1px solid var(--line)", borderTopColor: "var(--gold)" }} />
        <span style={{ fontSize: 12, fontWeight: 300 }}>Cargando cursos…</span>
      </div>
    );
  }

  if (!cursos.length) {
    return (
      <div style={{ padding: "16px 0", fontSize: 12, color: "var(--ink-3)", fontStyle: "italic" }}>
        No se encontraron cursos en la API.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0, border: "1px solid var(--line)", overflow: "hidden" }}>
      {/* Header row */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 160px 28px",
        padding: "8px 14px",
        background: "var(--bg-2)",
        borderBottom: "1px solid var(--line)",
      }}>
        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ink-3)" }}>Curso</span>
        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ink-3)" }}>Pixel ID</span>
        <span />
      </div>

      {/* Rows */}
      {cursos.map((curso, i) => {
        const pid = pixelMap[curso.id] || "";
        const hasPixel = pid.trim().length >= 10;
        const isOpen = expanded === curso.id;

        return (
          <div
            key={curso.id}
            style={{
              borderBottom: i < cursos.length - 1 ? "1px solid var(--line)" : "none",
              background: isOpen ? "rgba(201,168,76,0.03)" : "var(--bg-3)",
              transition: "background 0.2s",
            }}
          >
            {/* Main row */}
            <div
              style={{
                display: "grid", gridTemplateColumns: "1fr 160px 28px",
                alignItems: "center", padding: "10px 14px", gap: 10,
                cursor: "pointer",
              }}
              onClick={() => setExpanded(isOpen ? null : curso.id)}
            >
              {/* Name + badge */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <div style={{
                  width: 28, height: 28, flexShrink: 0, overflow: "hidden",
                  border: "1px solid var(--line)", background: "var(--bg-2)",
                }}>
                  {curso.imagen
                    ? <img src={curso.imagen} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <FiShoppingCart size={10} style={{ color: "var(--gold-brd)" }} />
                      </div>
                  }
                </div>
                <p style={{
                  fontSize: 12, fontWeight: 600, color: "var(--ink)",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {curso.nombre_curso || "Sin nombre"}
                </p>
                {hasPixel && <Badge variant="ok">✓</Badge>}
              </div>

              {/* Pixel preview */}
              <div style={{ minWidth: 0 }}>
                {pid ? (
                  <span style={{
                    fontFamily: "'Courier New', monospace", fontSize: 11,
                    color: hasPixel ? "var(--gold)" : "var(--ink-3)",
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    display: "block",
                  }}>
                    {pid}
                  </span>
                ) : (
                  <span style={{ fontSize: 11, color: "var(--ink-3)", fontStyle: "italic" }}>Sin asignar</span>
                )}
              </div>

              {/* Expand chevron */}
              <div style={{ display: "flex", justifyContent: "center", color: "var(--gold-dim)" }}>
                {isOpen ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />}
              </div>
            </div>

            {/* Expanded input */}
            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                  style={{ overflow: "hidden" }}
                >
                  <div style={{ padding: "0 14px 14px", display: "flex", gap: 8, alignItems: "center" }}>
                    <div style={{ flex: 1 }}>
                      <StyledInput
                        value={pid}
                        onChange={(e) => onPixelChange(curso.id, e.target.value)}
                        placeholder="Pixel ID para este curso (ej: 1234567890123456)"
                        mono
                      />
                    </div>
                    {pid && (
                      <motion.button
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        whileHover={{ opacity: 0.7 }} whileTap={{ scale: 0.95 }}
                        onClick={() => onPixelChange(curso.id, "")}
                        style={{
                          width: 36, height: 40, display: "flex", alignItems: "center", justifyContent: "center",
                          background: "transparent", border: "1px solid rgba(224,122,122,0.25)",
                          color: "var(--red)", cursor: "pointer", flexShrink: 0,
                        }}
                      >
                        <FiX size={12} />
                      </motion.button>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}

      {/* Footer summary */}
      <div style={{
        padding: "8px 14px", background: "var(--bg-2)",
        borderTop: "1px solid var(--line)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <span style={{ fontSize: 10, fontWeight: 300, color: "var(--ink-3)" }}>
          {Object.values(pixelMap).filter((v) => v && v.trim().length >= 10).length} de {cursos.length} cursos con pixel asignado
        </span>
        <div style={{ display: "flex", gap: 4 }}>
          {cursos.map((c) => (
            <div
              key={c.id}
              title={c.nombre_curso}
              style={{
                width: 5, height: 5, borderRadius: "50%",
                background: pixelMap[c.id]?.trim().length >= 10 ? "var(--green)" : "var(--line)",
                transition: "background 0.3s",
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────
   TABS
───────────────────────────────────── */
const TABS = [
  { key: "eventos", label: "Eventos", icon: <FiZap size={12} /> },
  { key: "probar",  label: "Probar",  icon: <FiPlay size={12} /> },
];

/* ─────────────────────────────────────
   MOTION VARIANTS
───────────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

const tabVariant = {
  hidden: { opacity: 0, y: 10 },
  show:   { opacity: 1, y: 0,  transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } },
  exit:   { opacity: 0, y: -6, transition: { duration: 0.22, ease: [0.22, 1, 0.36, 1] } },
};

/* ═══════════════════════════════════════════════
   COMPONENTE PRINCIPAL
═══════════════════════════════════════════════ */
const AdminMetaPixel = () => {
  const [pixelInput, setPixelInput] = useState("");
  const [pixelId,    setPixelId]    = useState("");
  const [activeTab,  setActiveTab]  = useState("eventos");
  const [eventLog,   setEventLog]   = useState([]);
  const [toast,      setToast]      = useState({ show: false, msg: "", type: "ok" });
  const [showMenu,   setShowMenu]   = useState(false);

  /* Purchase config */
  const [events, setEvents] = useState({
    pv:  { active: true },
    pur: { active: false, value: "", currency: "ARS", content: "" },
  });

  /* Pixel mode: "global" | "individual" */
  const [pixelMode, setPixelMode] = useState("global");

  /* Cursos de la API + mapa pixel por curso */
  const [cursos,      setCursos]      = useState([]);
  const [cursosLoading, setCursosLoading] = useState(false);
  const [pixelMap,    setPixelMap]    = useState({});  // { [curso.id]: pixelId }

  /* Fetch cursos cuando se activa Purchase */
  useEffect(() => {
    if (events.pur.active) {
      setCursosLoading(true);
      fetch(CURSOS_API)
        .then((r) => r.json())
        .then((data) => {
          if (Array.isArray(data)) {
            setCursos(data);
            // inicializar map con pixel global si ya hay uno
            const init = {};
            data.forEach((c) => { init[c.id] = pixelMap[c.id] || ""; });
            setPixelMap((prev) => ({ ...init, ...prev }));
          }
        })
        .catch(() => {})
        .finally(() => setCursosLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events.pur.active]);

  /* helpers */
  const showToast = (msg, type = "ok") => {
    setToast({ show: true, msg, type });
    setTimeout(() => setToast((p) => ({ ...p, show: false })), 2800);
  };

  const savePixel = () => {
    const val = pixelInput.trim();
    if (!val || !/^\d{10,20}$/.test(val)) {
      showToast("Ingresá un Pixel ID válido (solo números, 10–20 dígitos)", "err");
      return;
    }
    setPixelId(val);
    showToast("Pixel ID guardado correctamente");
  };

  const toggleEvent = (key) =>
    setEvents((prev) => ({ ...prev, [key]: { ...prev[key], active: !prev[key].active } }));

  const updatePur = (field, val) =>
    setEvents((prev) => ({ ...prev, pur: { ...prev.pur, [field]: val } }));

  const logEvent = (name, extra = "") => {
    const now = new Date().toLocaleTimeString("es-AR");
    setEventLog((prev) => [...prev, { time: now, name, extra, id: Date.now() }]);
  };

  const handlePixelMapChange = (cursoId, val) =>
    setPixelMap((prev) => ({ ...prev, [cursoId]: val }));

  /* Determinar pixel efectivo por modo */
  const effectivePixelId = (cursoId) =>
    pixelMode === "global" ? pixelId : (pixelMap[cursoId] || "");

  /* checklist */
  const individualOk = pixelMode === "individual"
    ? cursos.length > 0 && Object.values(pixelMap).some((v) => v?.trim().length >= 10)
    : true;

  const checks = [
    { label: "Pixel ID configurado",              ok: !!pixelId },
    { label: "Evento PageView activo",            ok: events.pv.active },
    { label: "Evento Purchase activo",            ok: events.pur.active },
    { label: "Parámetros de Purchase definidos",  ok: events.pur.active && !!events.pur.value },
    ...(pixelMode === "individual" && events.pur.active
      ? [{ label: "Píxel asignado a al menos un curso", ok: individualOk }]
      : []),
  ];
  const allOk = checks.every((c) => c.ok);

  /* ─────────────────────────────────────
     RENDER
  ───────────────────────────────────── */
  return (
    <>
      <GlobalStyles />
      <div style={{ background: "var(--bg)", minHeight: "100vh", fontFamily: "'Plus Jakarta Sans', sans-serif", color: "var(--ink)" }}>

        {/* Top accent */}
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: 1, zIndex: 50, background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.5), transparent)" }} />

        {/* Radial glow */}
        <div style={{ position: "fixed", inset: 0, pointerEvents: "none", background: "radial-gradient(ellipse at 15% 0%, rgba(201,168,76,0.06) 0%, transparent 55%)" }} />

        <FloatingAdminMenu showMenu={showMenu} setShowMenu={setShowMenu} />

        <div style={{ maxWidth: 860, margin: "0 auto", padding: "clamp(64px,8vw,96px) clamp(16px,5vw,48px) 96px", position: "relative", zIndex: 10 }}>

          {/* ══ HEADER ══ */}
          <motion.div variants={fadeUp} initial="hidden" animate="show" style={{ marginBottom: 40 }}>
            <SectionLabel style={{ marginBottom: 12 }}>Integración publicitaria</SectionLabel>
            <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 8 }}>
                  <div style={{
                    width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center",
                    background: "linear-gradient(135deg, rgba(0,130,251,0.18), rgba(174,0,214,0.18))",
                    border: "1px solid rgba(107,163,224,0.3)",
                  }}>
                    <FaFacebookF size={20} style={{ color: "#0082FB" }} />
                  </div>
                  <h1 style={{ fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 800, color: "var(--ink)", letterSpacing: "-0.02em", lineHeight: 1.05 }}>
                    Meta <em style={{ color: "var(--gold)", fontStyle: "normal" }}>Ads</em> — Píxel Manager
                  </h1>
                </div>
                <p style={{ fontSize: 13, fontWeight: 300, color: "var(--ink-3)", fontStyle: "italic" }}>
                  Configurá y desplegá tu píxel de seguimiento en la landing de IDM Medicina
                </p>
              </div>
              <Badge variant={allOk ? "ok" : pixelId ? "warn" : "neutral"}>
                {allOk ? "✓ Listo" : pixelId ? "Incompleto" : "Sin configurar"}
              </Badge>
            </div>
            <motion.div
              style={{ height: 1, background: "linear-gradient(90deg, var(--gold), transparent)", opacity: 0.14, marginTop: 24 }}
              initial={{ scaleX: 0, originX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            />
          </motion.div>

          {/* ══ PIXEL ID GLOBAL ══ */}
          <motion.div variants={fadeUp} initial="hidden" animate="show" style={{ marginBottom: 28 }}>
            <SectionLabel style={{ marginBottom: 14 }}>1 · Identificador del píxel</SectionLabel>
            <div style={{ background: "var(--bg-3)", border: "1px solid var(--line)", padding: "clamp(20px,4vw,32px)", position: "relative" }}>
              <CardTopLine />
              <div style={{ display: "flex", gap: 10, alignItems: "stretch" }}>
                <div style={{ flex: 1 }}>
                  <StyledInput
                    value={pixelInput}
                    onChange={(e) => setPixelInput(e.target.value)}
                    placeholder="Ej: 1234567890123456"
                    mono
                  />
                </div>
                <motion.button
                  whileHover={{ opacity: 0.87 }} whileTap={{ scale: 0.97 }}
                  onClick={savePixel}
                  style={{
                    padding: "0 22px", background: "var(--gold)", color: "#0A0906",
                    border: "none", cursor: "pointer", fontSize: 11, fontWeight: 700,
                    letterSpacing: "0.14em", textTransform: "uppercase", height: 40,
                    display: "flex", alignItems: "center", gap: 6, flexShrink: 0,
                  }}
                >
                  <FiSave size={13} />
                  Guardar
                </motion.button>
              </div>
              <AnimatePresence>
                {pixelId && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12, flexWrap: "wrap" }}
                  >
                    <span style={{
                      fontFamily: "'Courier New', monospace", fontSize: 13, color: "var(--gold)",
                      background: "var(--gold-sub)", border: "1px solid var(--gold-brd)",
                      padding: "5px 12px", letterSpacing: "0.08em",
                    }}>
                      {pixelId}
                    </span>
                    <span style={{ fontSize: 11, color: "var(--ink-3)", fontWeight: 300, fontStyle: "italic" }}>
                      Píxel activo — se usa como base global
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>
              <Hint>
                Encontrás el Pixel ID en Meta Business Suite → Administrador de eventos → Píxeles. Es un número de 15–16 dígitos.
              </Hint>
            </div>
          </motion.div>

          {/* ══ TABS ══ */}
          <div style={{ display: "flex", borderBottom: "1px solid var(--line)", marginBottom: 24 }}>
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                style={{
                  padding: "10px 20px", background: "transparent", border: "none",
                  borderBottom: `2px solid ${activeTab === t.key ? "var(--gold)" : "transparent"}`,
                  color: activeTab === t.key ? "var(--gold)" : "var(--ink-3)",
                  fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase",
                  cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                  transition: "color 0.2s, border-color 0.2s",
                }}
              >
                {t.icon}
                {t.label}
              </button>
            ))}
          </div>

          {/* ══ CONTENIDO POR TAB ══ */}
          <AnimatePresence mode="wait">

            {/* ── TAB EVENTOS ── */}
            {activeTab === "eventos" && (
              <motion.div key="eventos" variants={tabVariant} initial="hidden" animate="show" exit="exit">
                <SectionLabel style={{ marginBottom: 16 }}>2 · Eventos a trackear</SectionLabel>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px,1fr))", gap: 14 }}>

                  {/* PageView */}
                  <div style={{
                    background: "var(--bg-3)",
                    border: `1px solid ${events.pv.active ? "rgba(107,163,224,0.3)" : "var(--line)"}`,
                    padding: "22px 24px", position: "relative", transition: "border-color 0.3s",
                  }}>
                    <CardTopLine color="rgba(107,163,224,0.22)" />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{
                          width: 38, height: 38, display: "flex", alignItems: "center", justifyContent: "center",
                          border: "1px solid var(--blue-brd)", background: "var(--blue-sub)", color: "var(--blue)",
                        }}>
                          <FiEye size={17} />
                        </div>
                        <div>
                          <p style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)", marginBottom: 2 }}>PageView</p>
                          <p style={{ fontSize: 10, color: "var(--ink-3)", letterSpacing: "0.12em", textTransform: "uppercase" }}>Vista de página</p>
                        </div>
                      </div>
                      <Toggle value={events.pv.active} onChange={() => toggleEvent("pv")} pulse />
                    </div>
                    <Divider />
                    <p style={{ fontSize: 12, fontWeight: 300, color: "var(--ink-2)", lineHeight: 1.75, marginBottom: 14 }}>
                      Se dispara automáticamente al cargar cada página. Esencial para medir alcance y construir audiencias de remarketing.
                    </p>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <Badge variant={events.pv.active ? "ok" : "neutral"}>{events.pv.active ? "✓ Activo" : "Inactivo"}</Badge>
                      <Badge variant="blue">Automático</Badge>
                    </div>
                  </div>

                  {/* Purchase */}
                  <div style={{
                    background: "var(--bg-3)",
                    border: `1px solid ${events.pur.active ? "rgba(109,191,143,0.3)" : "var(--line)"}`,
                    padding: "22px 24px", position: "relative", transition: "border-color 0.3s",
                  }}>
                    <CardTopLine color="rgba(109,191,143,0.22)" />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{
                          width: 38, height: 38, display: "flex", alignItems: "center", justifyContent: "center",
                          border: "1px solid var(--green-brd)", background: "var(--green-dim)", color: "var(--green)",
                        }}>
                          <FiShoppingCart size={17} />
                        </div>
                        <div>
                          <p style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)", marginBottom: 2 }}>Purchase</p>
                          <p style={{ fontSize: 10, color: "var(--ink-3)", letterSpacing: "0.12em", textTransform: "uppercase" }}>Evento de compra</p>
                        </div>
                      </div>
                      <Toggle value={events.pur.active} onChange={() => toggleEvent("pur")} pulse />
                    </div>
                    <Divider />
                    <p style={{ fontSize: 12, fontWeight: 300, color: "var(--ink-2)", lineHeight: 1.75, marginBottom: 14 }}>
                      Se dispara cuando el usuario completa una compra. Permite optimizar campañas por conversiones reales.
                    </p>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <Badge variant={events.pur.active ? "ok" : "neutral"}>{events.pur.active ? "✓ Activo" : "Inactivo"}</Badge>
                      <Badge variant="warn">Manual</Badge>
                    </div>

                    {/* Expanded Purchase config */}
                    <AnimatePresence>
                      {events.pur.active && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                          style={{ overflow: "hidden" }}
                        >
                          <Divider />

                          {/* ── MODO DE PIXEL ── */}
                          <SectionLabel style={{ marginBottom: 10 }}>Modo de asignación del píxel</SectionLabel>
                          <PixelModeSelector mode={pixelMode} onChange={setPixelMode} />

                          <div style={{ height: 14 }} />

                          {/* Global: parámetros simples */}
                          <AnimatePresence mode="wait">
                            {pixelMode === "global" && (
                              <motion.div
                                key="global-params"
                                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                              >
                                <SectionLabel style={{ marginBottom: 10 }}>Parámetros del evento</SectionLabel>
                                <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                                  <div style={{ flex: 1 }}>
                                    <p style={{ fontSize: 10, color: "var(--ink-3)", letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>Valor</p>
                                    <StyledInput
                                      value={events.pur.value}
                                      onChange={(e) => updatePur("value", e.target.value)}
                                      placeholder="29900"
                                      type="number"
                                    />
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <p style={{ fontSize: 10, color: "var(--ink-3)", letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>Moneda</p>
                                    <StyledInput
                                      value={events.pur.currency}
                                      onChange={(e) => updatePur("currency", e.target.value)}
                                      placeholder="ARS"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <p style={{ fontSize: 10, color: "var(--ink-3)", letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>Nombre del producto</p>
                                  <StyledInput
                                    value={events.pur.content}
                                    onChange={(e) => updatePur("content", e.target.value)}
                                    placeholder="Curso IDM"
                                  />
                                </div>
                                <Hint>
                                  Se usará el Pixel ID global para todos los eventos de compra.
                                </Hint>
                              </motion.div>
                            )}

                            {/* Individual: tabla de cursos */}
                            {pixelMode === "individual" && (
                              <motion.div
                                key="individual-params"
                                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                              >
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                                  <SectionLabel>Pixel por curso</SectionLabel>
                                  <motion.button
                                    whileHover={{ opacity: 0.7 }} whileTap={{ scale: 0.95 }}
                                    onClick={() => {
                                      setCursosLoading(true);
                                      fetch(CURSOS_API)
                                        .then((r) => r.json())
                                        .then((data) => { if (Array.isArray(data)) setCursos(data); })
                                        .catch(() => {})
                                        .finally(() => setCursosLoading(false));
                                    }}
                                    style={{
                                      display: "flex", alignItems: "center", gap: 5,
                                      background: "transparent", border: "1px solid var(--line)",
                                      color: "var(--gold-dim)", padding: "4px 10px", cursor: "pointer",
                                      fontSize: 10, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase",
                                    }}
                                  >
                                    <FiRefreshCw size={10} className={cursosLoading ? "adm-spin" : ""} />
                                    Actualizar
                                  </motion.button>
                                </div>

                                <CursosPixelTable
                                  cursos={cursos}
                                  pixelMap={pixelMap}
                                  onPixelChange={handlePixelMapChange}
                                  loading={cursosLoading}
                                />

                                <Hint>
                                  Hacé clic en un curso para asignarle su Pixel ID. Cada curso enviará eventos al pixel correspondiente.
                                </Hint>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                </div>
              </motion.div>
            )}

            {/* ── TAB PROBAR ── */}
            {activeTab === "probar" && (
              <motion.div key="probar" variants={tabVariant} initial="hidden" animate="show" exit="exit">
                <SectionLabel style={{ marginBottom: 20 }}>3 · Verificar integración</SectionLabel>

                {/* Simulación */}
                <div style={{ background: "var(--bg-3)", border: "1px solid var(--line)", padding: "22px 24px", marginBottom: 16, position: "relative" }}>
                  <CardTopLine />
                  <p style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)", marginBottom: 6 }}>Simulación de eventos</p>
                  <Hint>
                    Simulá el disparo de cada evento. En producción verificá con Meta Pixel Helper (extensión Chrome).
                  </Hint>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "18px 0" }}>
                    <motion.button
                      whileHover={{ background: "rgba(107,163,224,0.15)" }} whileTap={{ scale: 0.97 }}
                      onClick={() => logEvent("PageView")}
                      disabled={!events.pv.active}
                      style={{
                        padding: "10px 18px", background: "var(--blue-sub)", color: "var(--blue)",
                        border: "1px solid var(--blue-brd)", cursor: events.pv.active ? "pointer" : "not-allowed",
                        opacity: events.pv.active ? 1 : 0.35,
                        fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase",
                        display: "flex", alignItems: "center", gap: 6,
                      }}
                    >
                      <FiPlay size={11} />
                      Simular PageView
                    </motion.button>
                    <motion.button
                      whileHover={events.pur.active ? { background: "rgba(109,191,143,0.15)" } : {}}
                      whileTap={events.pur.active ? { scale: 0.97 } : {}}
                      onClick={() =>
                        events.pur.active &&
                        logEvent("Purchase", `value: ${events.pur.value || "29900"} ${events.pur.currency || "ARS"}, pixel: ${pixelMode === "global" ? (pixelId || "–") : "por curso"}`)
                      }
                      disabled={!events.pur.active}
                      style={{
                        padding: "10px 18px", background: "var(--green-dim)", color: "var(--green)",
                        border: "1px solid var(--green-brd)", cursor: events.pur.active ? "pointer" : "not-allowed",
                        opacity: events.pur.active ? 1 : 0.3,
                        fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase",
                        display: "flex", alignItems: "center", gap: 6,
                      }}
                    >
                      <FiPlay size={11} />
                      Simular Purchase
                    </motion.button>
                    {eventLog.length > 0 && (
                      <motion.button
                        whileHover={{ opacity: 0.7 }} whileTap={{ scale: 0.97 }}
                        onClick={() => setEventLog([])}
                        style={{
                          padding: "10px 14px", background: "transparent", color: "var(--ink-3)",
                          border: "1px solid var(--line)", cursor: "pointer",
                          fontSize: 11, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase",
                          display: "flex", alignItems: "center", gap: 5,
                        }}
                      >
                        <FiX size={11} />
                        Limpiar
                      </motion.button>
                    )}
                  </div>

                  {/* Terminal log */}
                  <div style={{
                    background: "var(--bg-2)", border: "1px solid var(--line)",
                    padding: 16, minHeight: 90,
                    fontFamily: "'Courier New', monospace", fontSize: 11,
                    color: "var(--ink-2)", lineHeight: 1.9,
                  }}>
                    {eventLog.length === 0 ? (
                      <span style={{ color: "var(--ink-3)", fontStyle: "italic" }}>// El log de eventos aparecerá aquí…</span>
                    ) : (
                      eventLog.map((e) => (
                        <div key={e.id}>
                          <span style={{ color: "var(--blue)" }}>[{e.time}]</span>
                          {" "}
                          <span style={{ color: "var(--gold)" }}>fbq('track', '{e.name}'</span>
                          {e.extra ? <span style={{ color: "var(--ink-2)" }}>, {`{ ${e.extra} }`}</span> : null}
                          <span style={{ color: "var(--gold)" }}>)</span>
                          {" → "}
                          <span style={{ color: "var(--green)" }}>✓ simulado</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Checklist */}
                <div style={{ background: "var(--bg-3)", border: "1px solid var(--line)", padding: "22px 24px", position: "relative" }}>
                  <CardTopLine />
                  <p style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)", marginBottom: 16 }}>Checklist de verificación</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {checks.map((c, i) => <CheckItem key={i} label={c.label} ok={c.ok} />)}
                  </div>

                  {/* Modo activo info */}
                  {events.pur.active && (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                      style={{ marginTop: 16, padding: "10px 14px", background: "var(--bg-2)", border: "1px solid var(--line)", display: "flex", alignItems: "center", gap: 10 }}
                    >
                      {pixelMode === "global" ? <FiGlobe size={13} style={{ color: "var(--blue)" }} /> : <FiTag size={13} style={{ color: "var(--gold)" }} />}
                      <p style={{ fontSize: 11, fontWeight: 300, color: "var(--ink-2)" }}>
                        Modo Purchase activo:{" "}
                        <strong style={{ fontWeight: 700, color: pixelMode === "global" ? "var(--blue)" : "var(--gold)" }}>
                          {pixelMode === "global" ? "Pixel global para todos los cursos" : `Pixel individual — ${Object.values(pixelMap).filter((v) => v?.trim().length >= 10).length} de ${cursos.length} cursos asignados`}
                        </strong>
                      </p>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>

        {/* ── TOAST ── */}
        <AnimatePresence>
          {toast.show && (
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              style={{
                position: "fixed", bottom: 24, right: 24, zIndex: 999,
                background: "var(--bg-3)",
                border: `1px solid ${toast.type === "err" ? "rgba(224,122,122,0.4)" : "var(--green-brd)"}`,
                color: toast.type === "err" ? "var(--red)" : "var(--green)",
                fontSize: 12, fontWeight: 600, padding: "12px 20px", letterSpacing: "0.06em",
                display: "flex", alignItems: "center", gap: 8,
              }}
            >
              {toast.type === "err" ? <FiAlertCircle size={13} /> : <FiCheck size={13} />}
              {toast.msg}
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </>
  );
};

export default AdminMetaPixel;